/**
 * Phase 362 — RichEmailEditor.
 *
 * Single rich-text editor used by every email module inside Building
 * Management: Settings → Email Templates, Building → Emails → Compose, and
 * the Resend flow in Email History.
 *
 * Built on react-quill-new (Quill 2.x, React 19 compatible). The toolbar
 * exposes the formatting controls the user requested:
 *
 *   • Bold / Italic / Underline / Strikethrough
 *   • Bullet & Numbered lists
 *   • Font size, font color, highlight color
 *   • Alignment (left / center / right / justify)
 *   • Indent +/-, Line height (line spacing)
 *   • Clear formatting, hyperlinks, undo / redo
 *
 * The editor stores the body as HTML. Existing plain-text templates with
 * `{{placeholder}}` markers continue to work — Quill renders the plain text
 * as `<p>...</p>` automatically, and the Brevo integration already
 * accepts HTML bodies.
 *
 * Usage:
 *   <RichEmailEditor value={body} onChange={setBody} minHeight="280px" />
 */
import React, { useMemo, useRef, useEffect, useId } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Phase 362 — Custom line-height format (Quill ships with indent + align but
// no built-in line-height. We register a class-based attributor so the
// toolbar can offer 1.0 / 1.15 / 1.5 / 2.0 line-spacing presets.)
const Parchment = Quill.import('parchment');
const LineHeightStyle = new Parchment.StyleAttributor('lineHeight', 'line-height', {
  scope: Parchment.Scope.INLINE,
  whitelist: ['1', '1.15', '1.5', '2', '2.5', '3'],
});
Quill.register({ 'formats/lineHeight': LineHeightStyle }, true);

// Slightly larger size whitelist than Quill's defaults — covers the
// pt-equivalents most committees use in their email templates.
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
Quill.register(SizeStyle, true);

// Phase 719 — Font Family selector.
//
// Why use the STYLE attributor (not the default class-based one):
// Gmail, Outlook web and most mobile clients strip <style> tags and
// external stylesheets. The default `attributors/class/font` writes
// `<span class="ql-font-arial">` which only renders when the
// matching CSS rule is present in the email — guaranteed to fail.
// `attributors/style/font` writes `<span style="font-family: Arial,
// sans-serif">` directly, which every email client supports.
//
// Each entry's `name` (what the user picks) is paired with a
// `font-family` stack that includes the canonical fallback so the
// recipient sees the closest match even if the named font isn't
// installed.
const FONT_FAMILIES = [
  { name: 'Arial',           stack: "Arial, Helvetica, sans-serif" },
  { name: 'Calibri',         stack: "Calibri, 'Segoe UI', Arial, sans-serif" },
  { name: 'Helvetica',       stack: "Helvetica, Arial, sans-serif" },
  { name: 'Verdana',         stack: "Verdana, Geneva, sans-serif" },
  { name: 'Tahoma',          stack: "Tahoma, Geneva, sans-serif" },
  { name: 'Georgia',         stack: "Georgia, 'Times New Roman', serif" },
  { name: 'Times New Roman', stack: "'Times New Roman', Times, serif" },
  { name: 'Trebuchet MS',    stack: "'Trebuchet MS', Tahoma, sans-serif" },
  { name: 'Courier New',     stack: "'Courier New', Courier, monospace" },
];

const FontStyle = Quill.import('attributors/style/font');
// Quill's Snow theme uses the lowercase, dash-joined token as the
// option's `value` attribute. We register the full font-family stack
// as the whitelist so Quill writes that exact string to the
// `style="font-family: ..."` attribute.
FontStyle.whitelist = FONT_FAMILIES.map((f) => f.stack);
Quill.register(FontStyle, true);

const TOOLBAR_ID_BASE = 'rich-email-toolbar';

export default function RichEmailEditor({
  value = '',
  onChange,
  placeholder = 'Type your email body…',
  minHeight = '260px',
  testId = 'rich-email-editor',
}) {
  // Phase 714 — Stable toolbar id from React's useId so the
  // <ReactQuill modules.toolbar.container> reference stays valid for
  // the lifetime of this editor instance, even across parent re-renders.
  const reactId = useId();
  const toolbarId = useMemo(() => `${TOOLBAR_ID_BASE}-${reactId.replace(/:/g, '')}`, [reactId]);
  const quillRef = useRef(null);
  const toolbarRef = useRef(null);

  // Phase 714 — Fix the "Bold stays stuck" toggle bug.
  // The Quill-styled <button className="ql-bold" /> defaults to
  // type="submit" per the HTML spec. When this editor lives inside a
  // shadcn <Dialog> (or any parent that handles Enter/Submit), the
  // first Bold click could trigger an ambient form submission that
  // re-renders the dialog, losing the toolbar binding mid-toggle so
  // the second click never reaches Quill — exactly matching the
  // reported "Bold cannot be turned off" symptom. Adding
  // explicit type="button" to every toolbar <button> stops that.
  useEffect(() => {
    const tb = toolbarRef.current;
    if (!tb) return;
    tb.querySelectorAll('button').forEach((b) => {
      if (!b.getAttribute('type')) b.setAttribute('type', 'button');
    });
  }, [toolbarId]);

  // Stretch the editor area to a comfortable working height (Quill's
  // default container is short).
  useEffect(() => {
    const el = quillRef.current?.getEditor?.()?.root;
    if (el) el.style.minHeight = minHeight;
  }, [minHeight]);

  const modules = useMemo(() => {
    // Phase 714 — Comprehensive safe toolbar handlers.
    //
    // Why this exists: every default Quill toolbar handler starts with
    // `const range = this.quill.getSelection()` and then reads
    // `range.index` — if the editor lost focus (e.g. inside a shadcn
    // Dialog) the selection is `null` and a `TypeError: Cannot read
    // properties of null (reading 'index')` blows up the whole React
    // tree. We override EVERY format the toolbar exposes with a safe
    // wrapper that:
    //   1. Calls `quill.getSelection(true)` — `true` forces focus into
    //      the editor and always returns a valid range (collapsed
    //      caret at end of doc if the editor was blurred).
    //   2. Wraps the whole body in try/catch so a single bad
    //      keystroke can never throw to React's error boundary and
    //      blank the page.
    //   3. Reads the LIVE format via `getFormat(range)` to decide
    //      ON vs OFF — making every press a true toggle (matches the
    //      Gmail / Outlook / Word behaviour the user requested).

    const safeRange = (quill) => {
      try {
        const r = quill.getSelection(true);
        if (r) return r;
        // last resort — caret at end of doc
        const len = quill.getLength();
        quill.setSelection(Math.max(0, len - 1), 0, 'silent');
        return quill.getSelection(true);
      } catch (_e) {
        return null;
      }
    };

    const inlineToggle = (name) => function () {
      try {
        const quill = this.quill;
        if (!quill) return;
        const range = safeRange(quill);
        if (!range) return;
        const cur = quill.getFormat(range);
        quill.format(name, !cur[name], 'user');
      } catch (e) { console.warn(`[RichEmailEditor] ${name} toggle failed`, e); }
    };

    // Block-level formats whose value is the picker/select choice
    // (false/empty means "remove the format"). align, size, color,
    // background, lineHeight all fit this shape.
    const valueFormat = (name) => function (value) {
      try {
        const quill = this.quill;
        if (!quill) return;
        const range = safeRange(quill);
        if (!range) return;
        quill.format(name, value || false, 'user');
      } catch (e) { console.warn(`[RichEmailEditor] ${name} apply failed`, e); }
    };

    return {
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          bold: inlineToggle('bold'),
          italic: inlineToggle('italic'),
          underline: inlineToggle('underline'),
          strike: inlineToggle('strike'),
          color: valueFormat('color'),
          background: valueFormat('background'),
          size: valueFormat('size'),
          font: valueFormat('font'),
          align: valueFormat('align'),
          lineHeight: valueFormat('lineHeight'),
          list: function (value) {
            // Quill's `list` format toggles when the current value
            // matches the clicked button (e.g. clicking "bullet" on
            // an already-bulleted block removes the list).
            try {
              const quill = this.quill;
              if (!quill) return;
              const range = safeRange(quill);
              if (!range) return;
              const cur = quill.getFormat(range);
              quill.format('list', cur.list === value ? false : value, 'user');
            } catch (e) { console.warn('[RichEmailEditor] list failed', e); }
          },
          indent: function (value) {
            // Quill semantics: `+1` / `-1` are RELATIVE indent steps
            // (mirrors the default behaviour but null-safe).
            try {
              const quill = this.quill;
              if (!quill) return;
              const range = safeRange(quill);
              if (!range) return;
              const cur = quill.getFormat(range);
              const indent = parseInt(cur.indent || 0, 10);
              const mod = value === '+1' ? 1 : value === '-1' ? -1 : 0;
              const next = Math.max(0, indent + mod);
              quill.format('indent', next === 0 ? false : next, 'user');
            } catch (e) { console.warn('[RichEmailEditor] indent failed', e); }
          },
          link: function (value) {
            try {
              const quill = this.quill;
              if (!quill) return;
              const range = safeRange(quill);
              if (!range) return;
              if (value) {
                // Prompt only when user is turning a link ON (mirrors
                // Quill's native behaviour — but null-safe).
                const cur = quill.getFormat(range);
                const url = window.prompt('Enter URL:', cur.link || 'https://');
                if (url) quill.format('link', url, 'user');
              } else {
                quill.format('link', false, 'user');
              }
            } catch (e) { console.warn('[RichEmailEditor] link failed', e); }
          },
          clean: function () {
            try {
              const quill = this.quill;
              if (!quill) return;
              const range = safeRange(quill);
              if (!range) return;
              if (range.length > 0) {
                quill.removeFormat(range.index, range.length, 'user');
              } else {
                // Clear typing format keys one-by-one (Quill has no
                // bulk "clear typing format" API).
                const cur = quill.getFormat(range);
                Object.keys(cur).forEach((k) => quill.format(k, false, 'user'));
              }
            } catch (e) { console.warn('[RichEmailEditor] clean failed', e); }
          },
        },
      },
      history: { delay: 500, maxStack: 200, userOnly: true },
    };
  }, [toolbarId]);

  // Quill's `list` format covers both bulleted and ordered lists (the
  // toolbar `value` attribute on the button decides which). `clean` is a
  // toolbar action, not a registered format. Both must NOT appear in this
  // whitelist or Quill prints "Cannot register …" warnings on every render.
  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'list',
    'font', 'size', 'color', 'background',
    'align', 'indent', 'lineHeight',
    'link',
  ];

  return (
    <div className="rich-email-editor" data-testid={testId}>
      {/* Custom toolbar — one button per feature the user requested. */}
      <div ref={toolbarRef} id={toolbarId} className="ql-toolbar ql-snow">
        <span className="ql-formats">
          {/* Phase 719 — Font Family selector. Inline-style font so
              Gmail / Outlook / Apple Mail / mobile render the chosen
              face. Each <option> value is the full font-family stack
              (with web-safe fallback) that Quill writes verbatim into
              `style="font-family: ..."` on the wrapping <span>. */}
          <select className="ql-font" defaultValue={FONT_FAMILIES[0].stack} data-testid={`${testId}-font`}>
            {FONT_FAMILIES.map((f) => (
              <option key={f.name} value={f.stack} style={{ fontFamily: f.stack }}>
                {f.name}
              </option>
            ))}
          </select>
          <select className="ql-size" defaultValue="14px" data-testid={`${testId}-size`}>
            {SizeStyle.whitelist.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-bold" data-testid={`${testId}-bold`} />
          <button type="button" className="ql-italic" data-testid={`${testId}-italic`} />
          <button type="button" className="ql-underline" data-testid={`${testId}-underline`} />
          <button type="button" className="ql-strike" data-testid={`${testId}-strike`} />
        </span>
        <span className="ql-formats">
          <select className="ql-color" data-testid={`${testId}-color`} />
          <select className="ql-background" data-testid={`${testId}-highlight`} />
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-list" value="ordered" data-testid={`${testId}-ordered`} />
          <button type="button" className="ql-list" value="bullet" data-testid={`${testId}-bullet`} />
          <button type="button" className="ql-indent" value="-1" data-testid={`${testId}-outdent`} />
          <button type="button" className="ql-indent" value="+1" data-testid={`${testId}-indent`} />
        </span>
        <span className="ql-formats">
          <select className="ql-align" data-testid={`${testId}-align`} />
          <select className="ql-lineHeight" defaultValue="1.5" data-testid={`${testId}-lineheight`}>
            {LineHeightStyle.whitelist.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </span>
        <span className="ql-formats">
          <button type="button" className="ql-link" data-testid={`${testId}-link`} />
          <button type="button" className="ql-clean" data-testid={`${testId}-clean`} />
        </span>
        <span className="ql-formats">
          <button
            type="button"
            className="ql-undo"
            onClick={() => {
              try { quillRef.current?.getEditor?.()?.history?.undo?.(); }
              catch (e) { console.warn('[RichEmailEditor] undo failed', e); }
            }}
            title="Undo"
            data-testid={`${testId}-undo`}
            style={{ width: 28 }}
          >
            <svg viewBox="0 0 18 18">
              <polygon className="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10" />
              <path className="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9" />
            </svg>
          </button>
          <button
            type="button"
            className="ql-redo"
            onClick={() => {
              try { quillRef.current?.getEditor?.()?.history?.redo?.(); }
              catch (e) { console.warn('[RichEmailEditor] redo failed', e); }
            }}
            title="Redo"
            data-testid={`${testId}-redo`}
            style={{ width: 28 }}
          >
            <svg viewBox="0 0 18 18">
              <polygon className="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10" />
              <path className="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5" />
            </svg>
          </button>
        </span>
      </div>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={(html) => onChange && onChange(html)}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />

      <style>{`
        .rich-email-editor .ql-container.ql-snow { border-radius: 0; font-size: 13px; }
        /* Phase 994 — Keep the formatting toolbar visible while the user
           scrolls through a long email body. The toolbar sticks to the
           top of its nearest scroll container (the SendEmailDialog body
           wrapper uses overflow-y-auto). Z-index is set high enough for
           Quill's own font/size/color pickers to still layer above it. */
        .rich-email-editor .ql-toolbar.ql-snow {
          border-radius: 0;
          border-bottom: none;
          background: #fafafa;
          position: sticky;
          top: 0;
          z-index: 20;
        }
        /* The picker dropdowns must beat sticky-toolbar z-index so option
           lists still open above the toolbar row and above the body. */
        .rich-email-editor .ql-toolbar .ql-picker.ql-expanded .ql-picker-options { z-index: 30; }
        .rich-email-editor .ql-editor { min-height: ${minHeight}; padding: 12px 14px; }
        .rich-email-editor .ql-editor p { margin: 0 0 8px 0; }
        /* Phase 719 — Friendly font picker labels. The select's option
           values are the FULL font-family stacks (so emails render
           correctly) but the dropdown UI shows the human name. We
           render the label via ::before because Quill's Snow theme
           hides the native <option> text and uses ::before for the
           picker label/item display. */
        .rich-email-editor .ql-picker.ql-font { width: 140px; }
        .rich-email-editor .ql-picker.ql-font .ql-picker-label::before,
        .rich-email-editor .ql-picker.ql-font .ql-picker-item::before {
          content: 'Arial';
        }
        ${FONT_FAMILIES.map(f => `
          .rich-email-editor .ql-picker.ql-font .ql-picker-label[data-value="${f.stack}"]::before,
          .rich-email-editor .ql-picker.ql-font .ql-picker-item[data-value="${f.stack}"]::before {
            content: '${f.name}';
            font-family: ${f.stack};
          }
        `).join('\n')}
      `}</style>
    </div>
  );
}
